/* eslint-disable prettier/prettier */
import { zodResolver } from '@hookform/resolvers/zod';
import type { KeyboardEventHandler } from 'react';
import { useContext, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Form, useSearchParams } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { z } from 'zod';
import StatusCodes from 'http-status-codes';
import jwt_decode from 'jwt-decode';

import {
  InputWrapper,
  LoginFormButton,
  LoginFormFooter,
  LoginFormHeader,
  RequirementCheck
} from '../atoms';
import { getUserInfo, UserContext } from '../../contexts/UserContext';
import { APIEndPoints, Request } from '../../apis';
import { withNoAuthRedirect } from '../hocs/withNoAuthRedirect';
import { ChallengeNames } from '../../common/types/Authentication';
import { getTokenExpiry } from '../../common/utils/auth';

type TRegex = boolean | undefined;

const confirmResetPasswordSchema = z.object({
  confirmationCode: z.string().optional(),
  newPassword: z
    .string()
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])(?=.{8,})/,
      'Please enter a valid password'
    ),
  confirmNewPassword: z.string().min(1, 'Please confirm your password')
});

type ConfirmResetPasswordSchema = z.infer<typeof confirmResetPasswordSchema>;

const RequirementsWrapper = styled.div(
  ({ theme }) => css`
    border-top: ${theme.borders.light};
  `
);

const getSub = (decodedToken: unknown): string => {
  if (
    typeof decodedToken === 'object' &&
    decodedToken !== null &&
    'sub' in decodedToken
  ) {
    if (typeof decodedToken.sub === 'string') {
      return decodedToken.sub;
    }
  }
  return 'no-sub-found';
};

const ConfirmResetPasswordPage = () => {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<ConfirmResetPasswordSchema>({
    resolver: zodResolver(confirmResetPasswordSchema)
  });
  const [searchParams] = useSearchParams();

  const {
    loginData,
    updateLoginData,
    resetLoginData,
    onUserChange,
    setMerchantServiceUserInfo
  } = useContext(UserContext);

  const [errorMessageNewPassword, setErrorMessageNewPassword] = useState('');
  const [errorMessageConfirmNewPassword, setErrorMessageConfirmNewPassword] =
    useState('');
  const [showLoginButton, setShowLoginButton] = useState(false);

  const [resetingPassword, setResetingPassword] = useState(false);

  const isInitialPassword = searchParams.get('set-initial-password') === 'true';

  const regexEight = new RegExp('^.{8,}$');
  const regexLowerAndUpper = new RegExp('^(?=.*[a-z])(?=.*[A-Z])');
  const regexNumber = new RegExp('^(?=.*[0-9])');
  const regexSpecial = new RegExp('^(?=.*[!@#$%^&*])');

  const [hasEight, setHasEight] = useState<TRegex>(undefined);
  const [hasLowerAndUpper, setHasLowerAndUpper] = useState<TRegex>(undefined);
  const [hasNumber, setHasNumber] = useState<TRegex>(undefined);
  const [hasSpecial, setHasSpecial] = useState<TRegex>(undefined);

  const handleChange: KeyboardEventHandler<HTMLInputElement> = (e) => {
    setHasEight(regexEight.test(e.currentTarget.value));

    setHasLowerAndUpper(regexLowerAndUpper.test(e.currentTarget.value));

    setHasNumber(regexNumber.test(e.currentTarget.value));

    setHasSpecial(regexSpecial.test(e.currentTarget.value));
  };

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const onSubmit = async (data: any) => {
    const { newPassword, confirmNewPassword } = data;

    setErrorMessageNewPassword('');
    setErrorMessageConfirmNewPassword('');

    if (!hasEight) {
      setErrorMessageNewPassword('Your password must be at least 8 characters');
      return;
    }

    if (!hasLowerAndUpper) {
      setErrorMessageNewPassword(
        'Your password must have at least 1 uppercase and lowercase character'
      );
      return;
    }

    if (!hasNumber) {
      setErrorMessageNewPassword('Your password must have at least 1 number');
      return;
    }

    if (!hasSpecial) {
      setErrorMessageNewPassword(
        'Your password must have at least 1 special character'
      );
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setErrorMessageConfirmNewPassword('Passwords do not match');
      return;
    }

    setResetingPassword(true);

    try {
      const { email, session, challengeName } = loginData;

      const response = await Request.post(
        `${APIEndPoints.resetPasswordChallenge}/${encodeURIComponent(email)}`,
        z.any(),
        // SignInTOSchema, // @TODO, test if this schema works
        {
          answer: newPassword,
          session,
          challengeName: challengeName
        }
      );
      setResetingPassword(false);
      const { signInResult, merchant } = response;
      if (signInResult) {
        const { challengeName, session, userRole } = signInResult;
        if (challengeName === ChallengeNames.SMS) {
          updateLoginData({
            session,
            email,
            challengeName
          });
        } else {
          const { AccessToken } = signInResult;
          const decodedToken = jwt_decode(AccessToken);
          const sub: string = getSub(decodedToken);

          onUserChange({
            merchantId: merchant,
            userType: userRole,
            sub: sub,
            email: loginData.email
          });

          const userInfo = await getUserInfo(sub);
          // TODO log
          //console.log('userInfo', userInfo);

          if (userInfo) {
            // TODO log
            //console.log('___userInfo: setMerchantServiceUserInfo');
            setMerchantServiceUserInfo(userInfo);
          } else {
            // TODO: remove this after testing / before MVP
            // TODO log
            // console.log(
            //   '___userInfo ERRROR... most likely user not found in local db'
            // );
          }
        }
      }
    } catch (err: any) {
      if (err.status === StatusCodes.UNAUTHORIZED) {
        setShowLoginButton(true);
      }
      setErrorMessageConfirmNewPassword(err.message);
      setResetingPassword(false);
    }
  };

  return (
    <div>
      <LoginFormHeader>
        <h1>Confirm new password</h1>
        {!isInitialPassword && (
          <p>
            Enter your email and we’ll send a code to confirm your password
            reset
          </p>
        )}
      </LoginFormHeader>

      <Form onSubmit={handleSubmit(onSubmit)} noValidate={true}>
        {!isInitialPassword && (
          <InputWrapper
            name="confirmationCode"
            type="text"
            label="Confirmation Code"
            required
            register={register}
          />
        )}
        <InputWrapper
          name="newPassword"
          type="password"
          label="New Password"
          required
          register={register}
          onKeyUp={handleChange}
          errorMessage={errors.newPassword?.message || errorMessageNewPassword}
        />
        <InputWrapper
          name="confirmNewPassword"
          type="password"
          label="Confirm New Password"
          required
          register={register}
          errorMessage={
            errors.confirmNewPassword?.message || errorMessageConfirmNewPassword
          }
        />
        <RequirementsWrapper>
          <p>
            <strong>Your password must:</strong>
          </p>
          <RequirementCheck
            label="Be a minimum length of 8 characters"
            checked={hasEight}
          />
          <RequirementCheck
            label="Include at least one number"
            checked={hasNumber}
          />
          <RequirementCheck
            label="Include at least one uppercase and lower case letter"
            checked={hasLowerAndUpper}
          />
          <RequirementCheck
            label="Include at least one special character"
            checked={hasSpecial}
          />
        </RequirementsWrapper>

        <LoginFormFooter
          horizontalSpacing="10px"
          verticalSpacing="0px"
          justify="center"
        >
          {showLoginButton ? (
            <LoginFormButton onClick={() => resetLoginData()} type="button">
              Login Again
            </LoginFormButton>
          ) : (
            <LoginFormButton disabled={resetingPassword} type="submit">
              Confirm new password
            </LoginFormButton>
          )}
        </LoginFormFooter>
      </Form>
    </div>
  );
};

const SecureConfirmResetPasswordPage = withNoAuthRedirect(
  ConfirmResetPasswordPage
);
export { SecureConfirmResetPasswordPage as ConfirmResetPasswordPage };
